import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Popover } from "@mui/material";
import { GridColumnHeaderParams } from "@mui/x-data-grid-pro";

import { CustomButton, IButtonVariant } from "../../components/CustomButton";
import { Icon, IconNameEnum, IconTypeEnum } from "../../components/Icon";
import { RootState } from "../../store";
import { Ordering } from "../../types/ordering";
import { IOrdering } from "../../types/pagination";

import styles from "./TableHeaderCell.module.scss";

interface TableHeaderCellProps {
  tableName: string;
  params: GridColumnHeaderParams;
  selectorOrdering: (state: RootState, fieldName: string) => Ordering | null;
  selectorFilterIsActive: (state: RootState, fieldName: string) => boolean;
  removeFilterUpdateAction: (fieldName: string) => void;
  selectFilters: (type: string) => JSX.Element | undefined;
  orderingUpdateAction: (ordering: IOrdering) => void;
}

export const TableHeaderCell: React.FC<TableHeaderCellProps> = ({
  tableName,
  params,
  selectorOrdering,
  selectorFilterIsActive,
  removeFilterUpdateAction,
  selectFilters,
  orderingUpdateAction,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ordering = useSelector((state: RootState) => selectorOrdering(state, params.field));
  const isFilterActive = useSelector((state: RootState) => selectorFilterIsActive(state, params.field));

  const changeSort = () => {
    const newOrdering = {
      orderBy: params.field,
      descending: ordering ? ordering !== Ordering.Desc : false,
    };

    dispatch(orderingUpdateAction(newOrdering));
  };

  const openFilters = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeFilters = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "filter-popover" : undefined;

  return (
    <div className={styles.container}>
      <p className={styles.title}>{t(`${tableName}.${params.field}`)}</p>
      <div className={styles.filtersContainer}>
        {selectFilters(params.field) && (
          <button className={styles.wrapperFilterIcon} id={id} onClick={openFilters}>
            {isFilterActive ? (
              <Icon name={IconNameEnum.filter} type={IconTypeEnum.grey} className={styles.icon} />
            ) : (
              <Icon name={IconNameEnum.filter} className={styles.icon} />
            )}
          </button>
        )}
        <button className={styles.wrapperFilterIcon} onClick={changeSort}>
          {ordering === Ordering.Asc && <Icon name={IconNameEnum.sortAsc} className={styles.icon} />}
          {ordering === Ordering.Desc && <Icon name={IconNameEnum.sortDesc} className={styles.icon} />}
          {!ordering && <Icon name={IconNameEnum.sortNone} className={styles.icon} />}
        </button>
      </div>
      <Popover
        className={styles.popover}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={closeFilters}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <h1>{t(`${tableName}.${params.field}`)}</h1>
        <div className={styles.filters}>{selectFilters(params.field)}</div>
        <CustomButton
          variant={IButtonVariant.textImportant}
          className={styles.textSize}
          onClick={() => {
            dispatch(removeFilterUpdateAction(params.field));
          }}
          text={t("collectionYardsVisits.filters.cancel")}
        />
      </Popover>
    </div>
  );
};
